<div class="admin-events-container" *ngIf="user?.isAdmin && !loading">
  <header class="admin-events-header">
    <h1>Admin Events</h1>
    <p>Manage and view all events in the system.</p>
    <div class="button-row">
      <button class="refresh-button" (click)="loadCurrentEvents()" [disabled]="loading">
        <mat-icon>refresh</mat-icon>
        Refresh Events
      </button>
      <button class="refresh-button" (click)="downloadCSV()" [disabled]="loading">
        <mat-icon>download</mat-icon>
        Events to CSV
      </button>
      <button class="refresh-button" *ngIf="user?.isOwner" (click)="openCsvUploadDialog()" [disabled]="loading">
        <mat-icon>upload</mat-icon>
        Developer Tools
      </button>
    </div>
    <div class="search-row" *ngIf="allEvents.length > 0">
      <div class="search-field">
        <mat-icon>search</mat-icon>
        <input
          type="text"
          [value]="searchQuery"
          (input)="onSearchInput($event)"
          placeholder="Search title, ID, requestor/leader, location, description"
          aria-label="Search events"
        />
        <button
          class="clear-search"
          type="button"
          (click)="clearSearch()"
          *ngIf="searchQuery"
          aria-label="Clear search"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>
    </header>

  <div class="events-stats" *ngIf="allEvents.length > 0">
    <button class="stat-card stat-card-button" type="button" (click)="setFilter('all')" [class.active]="activeFilter === 'all'" [attr.aria-pressed]="activeFilter === 'all'">
      <h3>Total Events</h3>
      <span class="stat-number">{{ getDisplayedTotalCount() }}</span>
      <span class="stat-ratio" *ngIf="shouldShowStatsRatio()">of {{ allEvents.length }}</span>
    </button>
    <button class="stat-card stat-card-button" type="button" (click)="setFilter('pending')" [class.active]="activeFilter === 'pending'" [attr.aria-pressed]="activeFilter === 'pending'">
      <h3>Pending</h3>
      <span class="stat-number pending">{{ getDisplayedPendingCount() }}</span>
      <span class="stat-ratio" *ngIf="shouldShowStatsRatio()">of {{ getPendingCount() }}</span>
    </button>
    <button class="stat-card stat-card-button" type="button" (click)="setFilter('approved')" [class.active]="activeFilter === 'approved'" [attr.aria-pressed]="activeFilter === 'approved'">
      <h3>Approved</h3>
      <span class="stat-number approved">{{ getDisplayedApprovedCount() }}</span>
      <span class="stat-ratio" *ngIf="shouldShowStatsRatio()">of {{ getApprovedCount() }}</span>
    </button>
  </div>

  <div class="events-legend-row" *ngIf="allEvents.length > 0">
    <div class="events-legend">
      <button
        class="legend-item legend-toggle past"
        type="button"
        (click)="toggleDateCategory('past')"
        [class.active]="dateCategoryFilters.past"
        [attr.aria-pressed]="dateCategoryFilters.past"
      >
        Past
      </button>
      <button
        class="legend-item legend-toggle today"
        type="button"
        (click)="toggleDateCategory('today')"
        [class.active]="dateCategoryFilters.today"
        [attr.aria-pressed]="dateCategoryFilters.today"
      >
        Today
      </button>
      <button
        class="legend-item legend-toggle future"
        type="button"
        (click)="toggleDateCategory('future')"
        [class.active]="dateCategoryFilters.future"
        [attr.aria-pressed]="dateCategoryFilters.future"
      >
        Upcoming
      </button>
    </div>

    <div class="status-filter" *ngIf="availableStatuses.length > 0">
      <button
        class="status-filter-toggle"
        type="button"
        (click)="toggleStatusDropdown()"
        [attr.aria-expanded]="statusDropdownOpen"
      >
        Status: {{ getStatusSummary() }}
        <mat-icon>arrow_drop_down</mat-icon>
      </button>
      <div class="status-filter-menu" *ngIf="statusDropdownOpen">
        <div class="status-filter-actions">
          <button type="button" (click)="selectAllStatuses()">All</button>
          <button type="button" (click)="clearAllStatuses()">None</button>
        </div>
        <label class="status-filter-item" *ngFor="let status of availableStatuses; trackBy: trackByStatus">
          <input
            type="checkbox"
            [checked]="statusFilters[status]"
            (change)="toggleStatusFilter(status)"
          />
          <span>{{ getDisplayStatus(status) }}</span>
        </label>
      </div>
    </div>
  </div>

  <section class="events-list" *ngIf="currentEvents.length > 0">
    <div class="event-card" *ngFor="let event of currentEvents; trackBy: trackByEventId" [ngClass]="'date-cat-' + (event.dateCategory || 'future')">
      <div class="event-header">
        <h3 class="event-title">
          {{ displayValue(event.displayTitle) }}
          <span class="relative-time" [class.today]="isEventToday(event)" [class.in-future]="isEventInFuture(event)">
            {{ getRelativeTimeDescription(event) }}
          </span>
        </h3>
        <span class="event-status" [ngClass]="getStatusClass(event.status)">
          {{ getDisplayStatus(event.status) }}
        </span>
      </div>

      <div class="event-details">
        <div class="event-info">
          <div class="info-row">
            ID:
            <span>{{ displayValue(event.event_id) }}</span>
          </div>
          <div class="info-row">
            <mat-icon>event</mat-icon>
            <span>{{ displayValue(event.displayDate) }} <span class="tz-inline" *ngIf="!isBlankValue(event.displayDate)">ET</span></span>
          </div>
          <div class="info-row">
            <mat-icon>schedule</mat-icon>
            <span>{{ displayValue(event.displayTime) }} <span class="tz-inline" *ngIf="!isBlankValue(event.displayTime)">ET</span></span>
          </div>
          <div class="info-row">
            <mat-icon>person</mat-icon>
            <span class="requestor-text responsive" matTooltip="{{ event.originalRequestor }}" matTooltipShowDelay="300">{{ displayValue(event.requestor) }}</span>
          </div>
          <div class="info-row">
            <mat-icon>location_on</mat-icon>
            <span>{{ displayValue(event.primary_presenting_location) }}</span>
          </div>
          <div class="info-row">
            <mat-icon>category</mat-icon>
            <span>{{ displayValue(event.request_type) }}</span>
          </div>
          <div class="info-row">
            <mat-icon>group</mat-icon>
            <span>{{ getParticipantsDisplay(event.expected_number_of_participants) }}</span>
          </div>
        </div>

        <div class="event-description">
          <h4>Description</h4>
          <p>{{ displayValue(event.event_description) }}</p>
        </div>
      </div>

      <div class="event-actions">
        <!-- Quick Action Buttons for Pending Events -->
        <!-- <div *ngIf="canApprove(event)" class="quick-actions">
          <button class="action-button approve-button" (click)="approveEvent(event)">
            <mat-icon>check_circle</mat-icon>
            Approve
          </button>
          <button class="action-button reject-button" (click)="rejectEvent(event)">
            <mat-icon>cancel</mat-icon>
            Cancel
          </button>
        </div> -->

        <!-- Standard Actions -->
        <div class="standard-actions">
          <button class="action-button view-button" (click)="editEvent(event)">
            <mat-icon>edit</mat-icon>
            View
          </button>
          <button class="action-button metrics-button" *ngIf="hasMetrics(event)" (click)="openMetricsDialog(event, $event)">
            <mat-icon>bar_chart</mat-icon>
            Metrics
          </button>
          <button class="action-button delete-button icon-only" (click)="deleteEvent(event, $event)" matTooltip="Delete Event">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      </div>
    </div>
  </section>

  <div class="no-events" *ngIf="!loading && currentEvents.length === 0">
    <mat-icon>event_busy</mat-icon>
    <h2>No Current Events</h2>
    <p>There are no current or upcoming events in the system.</p>
  </div>
</div>

<div class="admin-access-denied" *ngIf="!user?.isAdmin && !loading">
  <h1>Access Denied</h1>
  <p>You do not have admin permissions to view this page.</p>
</div>

<div class="loading-spinner" *ngIf="loading">
  <mat-spinner></mat-spinner>
  <p>Loading events...</p>
</div>